import { Button } from '@/components/_ui/Button'
import T from '@/components/Helpers/Translate'
import type { CosmicUsageAndContents } from '@/lib/cms/cosmic/types/CosmicBelowFoldType'
import { useLineClamp } from '@/lib/hooks/helpers/useLineClamp'
import Image from 'next/image'
import { useRef, useState } from 'react'

type Props = {
  usageAndContents?: CosmicUsageAndContents
}

export const ProductUsageAndContentsSection = ({ usageAndContents }: Props) => {
  const [selectedTab, setSelectedTab] = useState(0)
  const ref = useRef<HTMLDivElement | null>(null)
  const clamps = useLineClamp(ref, { lines: 15 })
  const [visible, setVisible] = useState(false)

  if (!usageAndContents || usageAndContents.hide) return null

  return (
    <div
      className="mx-auto flex flex-col px-5 py-24 lg:flex-row lg:gap-24"
      data-test="product-usage-section"
      style={{ backgroundColor: usageAndContents.backgroundColor }}
    >
      <div className="mb-10 flex max-w-full items-start justify-center lg:mb-0 lg:w-1/2 lg:max-w-[50%] lg:justify-end">
        <Image
          src={usageAndContents.image}
          height={525}
          width={525}
          alt={usageAndContents.image}
          data-test="product-usage-section-image"
        />
      </div>
      <div className="relative flex flex-col items-center justify-center lg:w-1/2 lg:max-w-[29.25rem] lg:justify-start">
        <div className="flex w-full flex-row items-end justify-around lg:justify-between">
          {usageAndContents.items?.map((tab, index) => (
            <div
              className="flex cursor-pointer flex-col items-center justify-center"
              key={`product-info-tab-${tab.title}`}
              onClick={() => {
                setSelectedTab(index)
                setVisible(false)
              }}
            >
              <div
                className={`flex min-w-[100px] justify-center text-center text-[15px] md:min-w-[132px] md:text-[17px] lg:text-[22px] ${
                  selectedTab === index && 'font-semibold'
                } ${selectedTab !== index && 'mb-[6px]'}`}
                data-test={`product-${tab.title
                  .replaceAll(/\s+/g, '-')
                  .toLowerCase()}-title`}
              >
                {tab.title}
              </div>
              {selectedTab === index && (
                <Image
                  src="/icons/underline.svg"
                  height={6}
                  width={71}
                  alt="Underline"
                  className="w-[71px] lg:w-[132px]"
                />
              )}
            </div>
          ))}
        </div>
        <div
          className={`inner-html-list-style-position mt-8	 min-w-full whitespace-pre-line p-2.5	 leading-[35px] lg:mt-10  lg:max-w-120 ${
            visible ? '' : 'line-clamp-[15]'
          }	`}
          data-test={`product-${usageAndContents.items?.[selectedTab].title
            .replaceAll(/\s+/g, '-')
            .toLowerCase()}-description`}
          dangerouslySetInnerHTML={{
            __html: usageAndContents.items?.[selectedTab].content || '',
          }}
          ref={ref}
        />

        {clamps && !visible && (
          <>
            <div
              className="absolute bottom-10 z-10 flex h-4 w-full items-center justify-center overflow-hidden text-center blur-sm"
              style={{ backgroundColor: usageAndContents.backgroundColor }}
            ></div>

            <div>
              <Button
                size="default"
                variant="link"
                onClick={() => setVisible(!visible)}
              >
                <T>read_more</T>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
